import experiment from 'experiment-amd'
import _ from 'lodash'

import editorServerFacade from '../../editorServerFacade/editorServerFacade'
import constants from '../../platform/common/constants'
import type {
    AppDefinitionId,
    ClientSpecMap,
    ClientSpecMapEntry,
    EditorClientSpecMapEntry
} from '@wix/document-services-types'

import clientSpecMapService from './clientSpecMapService'
import installedTpaAppsOnSiteService from './installedTpaAppsOnSiteService'
import pendingAppsService from './pendingAppsService'
import provisionUtils from '../utils/provisionUtils'

export interface App {
    appDefinitionId: AppDefinitionId
    sourceTemplateId?: string
    version?: string
    appVersion?: string
    applicationId?: string | number
    instanceId?: string
    appFields?: {
        installedVersion?: string
    }
}

export interface AppAction {
    type?: string
    appDefId?: string
    applicationId?: string | number
    instanceId?: string
    appVersion?: string
    version?: string
    sourceTemplateId?: string
    legacyParams?: {
        sourceTemplateId: string
    }
}

export interface AppsStateDelta {
    newlyAddedApps: AppAction[]
    newlyRemovedApps: string[]
}

export interface SettleData {
    actions?: AppAction[]
    siteRevision?: string
    maybeBranchId?: string
}

const EXPERIMENT_FLAG = 'dm_replaceToNewOSinstallAppApi'

const ACTION_TYPES = {
    PROVISION: 'provision',
    PROVISION_FROM_TEMPLATE: 'provisionFromSourceTemplate',
    ADD: 'add',
    REMOVE: 'remove',
    UPDATE: 'update',
    DISMISS: 'dismiss'
} as const

export const isNewApiEnabled = (): boolean => experiment.isOpen(EXPERIMENT_FLAG)

const isValidClientSpecEntry = (entry: ClientSpecMapEntry): entry is EditorClientSpecMapEntry => {
    return 'appDefinitionId' in entry && 'applicationId' in entry && 'instanceId' in entry
}

export const getSettleEndpoint = (): string =>
    isNewApiEnabled()
        ? editorServerFacade.ENDPOINTS.SETTLE_AND_CONDUCT_V2
        : editorServerFacade.ENDPOINTS.SETTLE_AND_CONDUCT

export const isDataEmpty = (data: SettleData | AppsStateDelta): boolean => {
    if (isNewApiEnabled()) {
        const delta = data as AppsStateDelta
        return _.isEmpty(delta.newlyRemovedApps) && _.isEmpty(delta.newlyAddedApps)
    }

    const settleData = data as SettleData
    return _.isEmpty(settleData.actions)
}

const getNewApplicationIdStartingFrom1k = (clientSpecMap: ClientSpecMap): number => {
    const currentLargestId = clientSpecMapService.getLargestApplicationId(clientSpecMap)
    return provisionUtils.generateAppFlowsLargestAppId(currentLargestId)
}

export const getProvisionActionsOfUniqueApps = (clientSpecMap: ClientSpecMap, apps: App[]): AppAction[] => {
    const uniqueAppIds = generateUniqueApplicationIdForAllApps(apps, clientSpecMap)
    return apps.map((app, index) =>
        createProvisionAction(
            clientSpecMap,
            app.appDefinitionId,
            app.version,
            app.sourceTemplateId,
            uniqueAppIds[index]
        )
    )
}

const generateUniqueApplicationIdForAllApps = (apps: App[], clientSpecMap: ClientSpecMap): number[] => {
    const uniqueAppIds: number[] = []

    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < apps.length; i++) {
        let newApplicationId = getNewApplicationIdStartingFrom1k(clientSpecMap)

        while (uniqueAppIds.includes(newApplicationId)) {
            newApplicationId = getNewApplicationIdStartingFrom1k(clientSpecMap)
        }

        uniqueAppIds.push(newApplicationId)
    }

    return uniqueAppIds
}

export const createProvisionAction = (
    clientSpecMap: ClientSpecMap,
    appDefinitionId: string,
    version: string | undefined,
    sourceTemplateId?: string,
    applicationId?: number
): AppAction => {
    const existingApp = _.find(clientSpecMap, {appDefinitionId}) as EditorClientSpecMapEntry | undefined

    if (isNewApiEnabled()) {
        const action: AppAction = {
            appDefId: appDefinitionId,
            version
        }

        if (sourceTemplateId) {
            action.legacyParams = {sourceTemplateId}
        }

        return action
    }

    // Legacy API handling
    if (sourceTemplateId) {
        return {
            appDefId: appDefinitionId,
            appVersion: version,
            sourceTemplateId,
            type: ACTION_TYPES.PROVISION_FROM_TEMPLATE
        }
    }

    return {
        appDefId: appDefinitionId,
        applicationId: existingApp?.applicationId || applicationId || getNewApplicationIdStartingFrom1k(clientSpecMap),
        appVersion: version,
        type: ACTION_TYPES.PROVISION
    }
}

const createAddAction = (
    appDefinitionId: string,
    version: string,
    applicationId: string | number,
    instanceId: string
): AppAction => {
    if (isNewApiEnabled()) {
        return {appDefId: appDefinitionId, version}
    }

    return {
        type: ACTION_TYPES.ADD,
        appDefId: appDefinitionId,
        appVersion: version,
        applicationId: _.toString(applicationId),
        instanceId
    }
}

export const createUpdateAction = (clientSpec: EditorClientSpecMapEntry): AppAction => {
    if (isNewApiEnabled()) {
        return {
            appDefId: clientSpec.appDefinitionId,
            version: 'version' in clientSpec ? clientSpec.version : undefined
        }
    }

    return {
        type: ACTION_TYPES.UPDATE,
        appDefId: clientSpec.appDefinitionId,
        applicationId: clientSpec.applicationId,
        instanceId: clientSpec.instanceId,
        appVersion: 'version' in clientSpec ? clientSpec.version : undefined
    }
}

const createRemoveAction = (appDefinitionId: string, applicationId: number | string): AppAction | string => {
    if (isNewApiEnabled()) {
        return appDefinitionId
    }

    return {
        type: ACTION_TYPES.REMOVE,
        applicationId: _.toString(applicationId)
    }
}

const createDismissAction = (appDefId: string): AppAction | string => {
    if (isNewApiEnabled()) {
        return appDefId
    }

    return {
        type: ACTION_TYPES.DISMISS,
        appDefId
    }
}

export const getProvisionActionsForAllPages = (
    clientSpecMap: ClientSpecMap,
    semanticAppVersions: Record<string, string> = {}
): AppAction[] => {
    const appsToProvision = installedTpaAppsOnSiteService.getAppsToProvision(clientSpecMap)

    return getProvisionActionsOfUniqueApps(
        clientSpecMap,
        Object.values(appsToProvision).map(({appDefinitionId}) => ({
            appDefinitionId,
            version: semanticAppVersions[appDefinitionId]
        }))
    )
}

export const formatAppsToAddFromAllPages = (
    appsToGrant: App[],
    appsToRevoke: App[],
    clientSpecMap: ClientSpecMap,
    semanticAppVersions: Record<string, string> = {},
    appsToProvision: AppAction[] = []
): AppAction[] => {
    const shouldIncludeApp = ({appDefinitionId}: App): boolean =>
        !appsToRevoke.some(app => app.appDefinitionId === appDefinitionId) &&
        !appsToProvision.some(app => app.appDefId === appDefinitionId)

    const formatApp = ({appDefinitionId, version, instanceId, applicationId, appFields}: App): AppAction => {
        const appVersion = appFields?.installedVersion || semanticAppVersions[appDefinitionId] || version
        const action = createAddAction(appDefinitionId, appVersion, applicationId, instanceId)
        return _.omitBy(action, _.isUndefined)
    }

    return appsToGrant.filter(shouldIncludeApp).map(formatApp)
}

export const formatAppsToRemove = (appsToRevoke: App[]): (AppAction | string)[] => {
    return appsToRevoke.map(({appDefinitionId, applicationId}) => createRemoveAction(appDefinitionId, applicationId))
}

export const formatAppsToDismiss = (): (AppAction | string)[] => {
    const dismissApps = pendingAppsService.getAppsToDismiss()
    return dismissApps.map(appDefId => createDismissAction(appDefId.toString()))
}

export const formatAppsToUpdate = (
    clientSpecMap: ClientSpecMap,
    appsToGrant: App[] = [],
    appsToRevoke: App[] = [],
    appsState: Record<string, {pendingAction?: string; unused?: boolean}> = {}
): AppAction[] => {
    const shouldUpdateApp = (entry: ClientSpecMapEntry): boolean => {
        if (!isValidClientSpecEntry(entry)) return false

        const appState = appsState?.[entry.appDefinitionId] || {}
        const isPendingUpdate = appState.pendingAction === constants.APP_ACTION_TYPES.UPDATE
        const isNotBeingGranted = !appsToGrant.some(app => app.appDefinitionId === entry.appDefinitionId)
        const isNotBeingRevoked = !appsToRevoke.some(app => app.appDefinitionId === entry.appDefinitionId)

        return isPendingUpdate && isNotBeingGranted && isNotBeingRevoked
    }

    return Object.values(clientSpecMap)
        .filter(shouldUpdateApp)
        .map(entry => createUpdateAction(entry as EditorClientSpecMapEntry))
}
